import { WELL_COLS, WELL_ROWS, idx, inBounds } from '../core/types';
import type { CellIndex } from '../core/types';

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface Insets {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export interface Layout {
  readonly width: number;
  readonly height: number;
  readonly portrait: boolean;
  readonly safe: Insets;
  readonly stage: Rect;

  readonly cell: number;
  readonly well: Rect;
  readonly wellX: number;
  readonly wellY: number;

  /**
   * Hero character rail — left side, vertically centred against the well.
   * Contains portrait, vertical HP bar, Block, Energy, statuses.
   */
  readonly heroRail: Rect;
  /** Vertical HP bar within the hero rail. */
  readonly heroHpBar: Rect;
  /** Portrait area within the hero rail. */
  readonly avatar: Rect;

  /**
   * Enemy info panels stacked vertically along the right side. Each section
   * holds portrait, vertical HP bar, compact intent, statuses.
   */
  readonly enemies: readonly Rect[];
  /** Vertical HP bars, one per enemy, aligned within each enemy rail section. */
  readonly enemyHpBars: readonly Rect[];
  /** Where enemy character busts are drawn. Overlaps the well's right edge. */
  readonly enemyBusts: readonly Rect[];
  /** Bounding box around the enemy rails. */
  readonly enemyZone: Rect;

  /**
   * Contextual info band between well and hand. Shows scoring preview,
   * card details, or status explanations when relevant. Collapses to
   * minimal height when nothing is selected.
   */
  readonly infoBand: Rect;

  readonly playerZone: Rect;
  /**
   * Minimal strip: End Turn, draw/discard icons. Nothing else permanently.
   */
  readonly status: Rect;
  /** Alias for status — where player-facing floats land. */
  readonly hud: Rect;
  readonly endTurn: Rect;
  readonly deckBadge: Rect;
  readonly discardBadge: Rect;
  readonly rotate: Rect;
  readonly cancel: Rect;

  /** Visible window onto the hand, not the whole deck. */
  readonly hand: Rect;
  readonly cards: readonly Rect[];
  readonly cardW: number;
  readonly cardH: number;
  readonly handContentW: number;
  readonly handVisible: number;

  readonly rewardCards: readonly Rect[];
  readonly skipButton: Rect;
}

const PAD = 12;
/**
 * Cards are vertical — the classic card proportion. A tetromino preview reads
 * well stacked above a short rules line, and the hand shows roughly three and
 * a half of them via the carousel.
 */
const CARD_W = 126;
const CARD_H = 180;
/** Fraction of a card's width left uncovered by its right-hand neighbour. */
const CARD_STEP = 0.77;

// --- safe areas -----------------------------------------------------------

let forcedSafe: Insets | null = null;

/** Debug override for the viewport harness. Pass null to restore device values. */
export function forceSafeArea(insets: Insets | null): void {
  forcedSafe = insets;
}

export function readSafeArea(): Insets {
  if (forcedSafe) return forcedSafe;
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }
  const cs = getComputedStyle(document.documentElement);
  const px = (name: string) => {
    const n = parseFloat(cs.getPropertyValue(name));
    return Number.isFinite(n) ? n : 0;
  };
  return { top: px('--sat'), right: px('--sar'), bottom: px('--sab'), left: px('--sal') };
}

// --- viewport harness -----------------------------------------------------

let forcedViewport: { width: number; height: number } | null = null;

export function forceViewport(v: { width: number; height: number } | null): void {
  forcedViewport = v;
}

export function forcedViewportSize(): { width: number; height: number } | null {
  return forcedViewport;
}

export const VIEWPORTS = {
  narrow: { width: 360, height: 740 },
  standard: { width: 390, height: 844 },
  tall: { width: 430, height: 932 },
  desktop: { width: 1440, height: 900 },
} as const;

let debugBounds = false;
export function setDebugBounds(on: boolean): void {
  debugBounds = on;
}
export function debugBoundsOn(): boolean {
  return debugBounds;
}

function rect(x: number, y: number, w: number, h: number): Rect {
  return { x, y, w, h };
}

/**
 * Side-layout combat composition.
 *
 * The combat arena is aspect-constrained and centered. On wide viewports the
 * extra space is background, not HUD real estate. The hero/well/enemy
 * relationship is spatially invariant: hero rail hugs the left of the well,
 * enemy rails hug the right, hand stays below.
 *
 *   portrait  █│  ┌──────────┐  │█  portrait
 *             █│  │          │  │█
 *       76    █│  │   WELL   │  │█  22/30
 *             ▒│  │          │  │░  <- aimed damage
 *      ☠ 3    │  └──────────┘  │   ATK 6
 *
 *              contextual info band
 *              card hand
 *
 * HP bars are narrow (12-18px) and sit directly adjacent to the well —
 * they read as the edges of the battlefield.
 */
export const MAX_COMBAT_W = 820;

export function computeLayout(
  width: number,
  height: number,
  cardCount: number,
  enemyCount = 1,
  handScroll = 0,
): Layout {
  const safe = readSafeArea();
  const rawW = Math.max(1, width - safe.left - safe.right);
  const stageH = Math.max(1, height - safe.top - safe.bottom);

  // Constrain the combat arena. Extra viewport width is background.
  const MAX_ASPECT = 9 / 19.5;
  let colW = rawW;
  if (colW > MAX_COMBAT_W) colW = MAX_COMBAT_W;
  if (colW / stageH > MAX_ASPECT) colW = Math.round(stageH * MAX_ASPECT);
  const colX = Math.round(safe.left + (rawW - colW) / 2);
  const stage = rect(colX, safe.top, colW, stageH);
  const portrait = height >= width;

  // --- vertical composition: content-packed, centered --------------------
  // The stack is: well → contextual band → status strip → hand.
  // Each section is sized by its content, not stretched to fill the viewport.
  // Extra height becomes background above/below the entire arena.

  const infoBandH = 30;
  const statusH = 28;
  const gap = 24;

  // Hand height from card geometry, not from remaining space.
  const CARD_H_TARGET = 160;
  const handPad = 8;
  const handContentH = CARD_H_TARGET + handPad;

  // Total content height.
  const chromeVertical = infoBandH + statusH + handContentH + gap * 3 + PAD;
  const availForWell = stageH - chromeVertical;

  // --- well sizing --------------------------------------------------------
  const hpBarW = 14;
  const railContentW = Math.max(44, Math.min(64, Math.round(stage.w * 0.13)));
  const railGap = 4;
  const chromeW = (railContentW + hpBarW + railGap) * 2;
  const availW = stage.w - chromeW - PAD;

  const cell = Math.max(10, Math.floor(Math.min(availW / WELL_COLS, availForWell / WELL_ROWS)));
  const wellW = cell * WELL_COLS;
  const wellH = cell * WELL_ROWS;

  // Content-packed total height.
  const totalH = wellH + gap + infoBandH + gap + statusH + gap + handContentH;
  // Center the entire stack vertically.
  const stackY = Math.round(safe.top + (stageH - totalH) / 2);

  // --- well position ------------------------------------------------------
  const contentW = chromeW + wellW;
  const startX = Math.round(stage.x + (stage.w - contentW) / 2);
  const wellX = startX + railContentW + hpBarW + railGap;
  const wellY = stackY;
  const well = rect(wellX, wellY, wellW, wellH);

  // --- rails: bars hug the well, content is derived outward from them -----
  // Bars frame the combatants and never cover them. Content rects are
  // computed FROM the bar rects, so the separation holds by construction:
  //
  //   content  █  │ well │  █  content

  // Hero HP bar: immediately left of the well.
  const heroHpBarH = Math.round(wellH * 0.8);
  const heroHpBarY = wellY + Math.round((wellH - heroHpBarH) / 2);
  const heroHpBar = rect(wellX - railGap - hpBarW, heroHpBarY, hpBarW, heroHpBarH);

  // Hero content: starts at the rail zone's left edge, stops at the bar.
  const heroH = Math.min(wellH, Math.round(wellH * 0.85));
  const heroY = wellY + Math.round((wellH - heroH) / 2);
  const heroRailW = Math.max(0, heroHpBar.x - startX);
  const heroRail = rect(startX, heroY, heroRailW, heroH);

  const avatarSize = Math.min(heroRailW - 6, Math.round(heroH * 0.26));
  const avatar = rect(
    heroRail.x + (heroRailW - avatarSize) / 2,
    heroRail.y + 6,
    avatarSize,
    avatarSize,
  );

  // --- enemy rails --------------------------------------------------------
  const nEnemies = Math.max(1, Math.min(enemyCount, 4));
  const enemyHpBars: Rect[] = [];
  const enemies: Rect[] = [];
  const enemyBusts: Rect[] = [];
  const eGap = 4;
  const eAvailH = wellH - eGap * (nEnemies - 1);
  const eRailH = Math.floor(eAvailH / nEnemies);
  const bustOverlap = Math.round(cell * 0.4);

  for (let i = 0; i < nEnemies; i++) {
    const ey = wellY + i * (eRailH + eGap);
    const eHpBarH = Math.round(eRailH * 0.7);
    const eHpBarY = ey + Math.round((eRailH - eHpBarH) / 2);
    // Bar hugs the well's right edge.
    const bar = rect(wellX + wellW + railGap, eHpBarY, hpBarW, eHpBarH);
    enemyHpBars.push(bar);
    // Content starts right of the bar — intent, HP number, badges, never under it.
    const contentX = bar.x + bar.w + railGap;
    const content = rect(contentX, ey, Math.max(0, railContentW), eRailH);
    enemies.push(content);
    // Bust overlaps from inside the well, passes behind the bar, and ends at
    // the content zone's outer edge. A drawing target only — the bar paints
    // over it; the content rect stays clear.
    const bustX = wellX + wellW - bustOverlap;
    const bustW = content.x + content.w - bustX;
    enemyBusts.push(rect(bustX, ey, bustW, eRailH));
  }
  const enemyZone = rect(wellX + wellW + railGap, wellY, hpBarW + railGap + railContentW, wellH);

  // --- info band (below well) ---------------------------------------------
  const infoBandY = wellY + wellH + gap;
  const infoBand = rect(stage.x + PAD, infoBandY, stage.w - PAD * 2, infoBandH);

  // --- status strip -------------------------------------------------------
  const statusY = infoBandY + infoBandH + gap;
  const status = rect(stage.x + PAD, statusY, stage.w - PAD * 2, statusH);
  const playerZoneY = wellY + wellH + gap;
  const playerZone = rect(stage.x, playerZoneY, stage.w, infoBandH + gap + statusH + gap + handContentH);

  const endTurnW = 68;
  const endTurn = rect(status.x + status.w - endTurnW, status.y, endTurnW, statusH);

  const badgeW = 26;
  const deckBadge = rect(status.x + 4, status.y + statusH / 2 - 10, badgeW, 20);
  const discardBadge = rect(deckBadge.x + badgeW + 4, status.y + statusH / 2 - 10, badgeW, 20);

  const toolW = 68;
  const rotate = rect(status.x, status.y + statusH + 4, toolW, 26);
  const cancel = rect(rotate.x + toolW + 6, rotate.y, toolW, 26);

  // --- hand carousel (content-sized, attached to battlefield) -------------
  const handY = statusY + statusH + gap;
  const hand = rect(stage.x, handY, stage.w, handContentH);

  const n = Math.max(cardCount, 1);
  const step = Math.round(CARD_W * CARD_STEP);
  const windowW = stage.w - PAD * 2;
  const handVisible = Math.max(1, windowW / Math.max(1, step));

  const contentW2 = CARD_W + step * Math.max(0, n - 1);
  const maxScroll = Math.max(0, contentW2 - windowW);
  const scroll = Math.max(0, Math.min(handScroll, maxScroll));
  const baseOffset = contentW2 <= windowW ? Math.round((windowW - contentW2) / 2) : 0;

  const cards: Rect[] = [];
  const handCardH = Math.max(CARD_W + 20, Math.min(CARD_H, handContentH - handPad));
  for (let i = 0; i < n; i++) {
    const x = stage.x + PAD + baseOffset + step * i - scroll;
    cards.push(rect(x, hand.y + 4, CARD_W, handCardH));
  }

  // --- reward screen ------------------------------------------------------
  const rW = Math.min(150, Math.floor((stage.w - PAD * 3) / 2));
  const rH = Math.min(200, Math.floor(stage.h * 0.32));
  const rGap = PAD;
  const gridW = rW * 2 + rGap;
  const rStartX = Math.round(stage.x + (stage.w - gridW) / 2);
  const rStartY = Math.round(stage.y + stage.h * 0.24);
  const rewardCards: Rect[] = [];
  for (let i = 0; i < 4; i++) {
    const cx = i % 2;
    const cy = Math.floor(i / 2);
    rewardCards.push(rect(rStartX + cx * (rW + rGap), rStartY + cy * (rH + rGap), rW, rH));
  }

  const skipButton = rect(
    Math.round(stage.x + (stage.w - 200) / 2),
    Math.round(rStartY + rH * 2 + rGap + 12),
    200,
    42,
  );

  return {
    width,
    height,
    portrait,
    safe,
    stage,
    cell,
    well,
    wellX,
    wellY,
    heroRail,
    heroHpBar,
    avatar,
    enemyZone,
    enemies,
    enemyHpBars,
    enemyBusts,
    playerZone,
    infoBand,
    status,
    hud: status,
    endTurn,
    deckBadge,
    discardBadge,
    rotate,
    cancel,
    hand,
    cards,
    cardW: CARD_W,
    cardH: handCardH,
    handContentW: contentW2,
    handVisible,
    rewardCards,
    skipButton,
  };
}

export function cellAt(l: Layout, x: number, y: number): CellIndex | null {
  const c = Math.floor((x - l.wellX) / l.cell);
  const r = Math.floor((y - l.wellY) / l.cell);
  if (!inBounds(r, c)) return null;
  return idx(r, c);
}

export function columnAt(l: Layout, x: number, y: number): number | null {
  if (y < l.wellY || y > l.wellY + l.well.h) return null;
  const c = Math.floor((x - l.wellX) / l.cell);
  return c >= 0 && c < WELL_COLS ? c : null;
}

export function contains(r: Rect, x: number, y: number): boolean {
  return x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h;
}

/**
 * The seed badge on the title and end screens. Shared by the renderer and the
 * hit-tester so clicking the seed copies it wherever it is drawn.
 */
export function seedBadgeRect(l: Layout): Rect {
  return rect(
    Math.round(l.stage.x + (l.stage.w - 280) / 2),
    Math.round(l.stage.y + l.stage.h - 72),
    280,
    32,
  );
}

/**
 * Grid of clickable deck cards on the socket screen. Shared by the renderer
 * and the hit-tester so the two can never disagree about where a card is.
 */
export function socketCardRects(l: Layout, count: number): Rect[] {
  const cols = 4;
  const gap = 10;
  const avail = l.stage.w - PAD * 2;
  const w = Math.min(124, Math.floor((avail - gap * (cols - 1)) / cols));
  const h = Math.round(w * 1.32);
  const rows = Math.max(1, Math.ceil(count / cols));
  const gridW = cols * w + (cols - 1) * gap;
  const startX = Math.round(l.stage.x + (l.stage.w - gridW) / 2);
  const startY = Math.round(l.stage.y + l.stage.h * 0.3);

  const out: Rect[] = [];
  for (let i = 0; i < count; i++) {
    const cx = i % cols;
    const cy = Math.floor(i / cols) % rows;
    out.push(rect(startX + cx * (w + gap), startY + cy * (h + gap), w, h));
  }
  return out;
}

/**
 * Vertical option cards on the rest/forge screen — one per rest action.
 * Shared by the renderer and the hit-tester.
 */
export function restOptionRects(l: Layout): Rect[] {
  const cardW = Math.min(300, l.stage.w - PAD * 4);
  const cardH = 58;
  const gap = 12;
  const startX = Math.round(l.stage.x + (l.stage.w - cardW) / 2);
  const startY = Math.round(l.stage.y + l.stage.h * 0.22);

  const out: Rect[] = [];
  for (let i = 0; i < 4; i++) {
    out.push(rect(startX, startY + i * (cardH + gap), cardW, cardH));
  }
  return out;
}

/**
 * Recipe cards on the craft screen. Shared by the renderer and the
 * hit-tester so they can never disagree about where a recipe is.
 */
export function craftOptionRects(l: Layout, count: number): Rect[] {
  const cardW = Math.min(320, l.stage.w - PAD * 4);
  const cardH = 64;
  const gap = 10;
  const startX = Math.round(l.stage.x + (l.stage.w - cardW) / 2);
  const startY = Math.round(l.stage.y + l.stage.h * 0.2);

  const out: Rect[] = [];
  for (let i = 0; i < count; i++) {
    out.push(rect(startX, startY + i * (cardH + gap), cardW, cardH));
  }
  return out;
}

/** Back-to-rest button on the craft screen. */
export function craftBackRect(l: Layout): Rect {
  return rect(
    Math.round(l.stage.x + (l.stage.w - 220) / 2),
    Math.round(l.stage.y + l.stage.h - 52),
    220,
    32,
  );
}

/**
 * Deck card rows on the purge screen. Shared by the renderer and the
 * hit-tester so they can never disagree about where a card is.
 */
export function purgeCardRects(l: Layout, count: number): Rect[] {
  const cols = 2;
  const gap = 8;
  const avail = l.stage.w - PAD * 4;
  const w = Math.min(200, Math.floor((avail - gap * (cols - 1)) / cols));
  const h = 32;
  const startX = Math.round(l.stage.x + (l.stage.w - (cols * w + gap)) / 2);
  const startY = Math.round(l.stage.y + l.stage.h * 0.24);

  const out: Rect[] = [];
  for (let i = 0; i < count; i++) {
    const cx = i % cols;
    const cy = Math.floor(i / cols);
    out.push(rect(startX + cx * (w + gap), startY + cy * (h + gap), w, h));
  }
  return out;
}

/**
 * Node-graph region on the map screen. The stage minus a title strip above
 * and an HP/notice strip below. Shared by the renderer and the hit-tester.
 */
export function mapGraphArea(l: Layout): Rect {
  const pad = 16;
  const top = 56;
  const bottom = 96;
  return rect(
    l.stage.x + pad,
    l.stage.y + top,
    Math.max(40, l.stage.w - pad * 2),
    Math.max(80, l.stage.h - top - bottom),
  );
}

/**
 * Boss preview panel on the map screen — top-right corner. Shows boss name,
 * alignment, and hint so the player can plan routes.
 */
export function bossPreviewRect(l: Layout): Rect {
  const w = Math.min(260, Math.max(160, l.stage.w * 0.42));
  const h = 54;
  return rect(
    l.stage.x + l.stage.w - w - 12,
    l.stage.y + 6,
    w,
    h,
  );
}

/**
 * Region legend on the map screen — bottom-left, just above the HP strip.
 * Only regions present on visible nodes are shown.
 */
export function regionLegendRect(l: Layout): Rect {
  return rect(
    l.stage.x + 12,
    l.stage.y + l.stage.h - 84,
    Math.min(320, l.stage.w - 24),
    20,
  );
}

/**
 * Vertical option cards on the elite cache screen. Shared by the renderer
 * and the hit-tester.
 */
export function cacheOptionRects(l: Layout, count: number): Rect[] {
  const cardW = Math.min(300, l.stage.w - PAD * 4);
  const cardH = 64;
  const gap = 16;
  const startX = Math.round(l.stage.x + (l.stage.w - cardW) / 2);
  const startY = Math.round(l.stage.y + l.stage.h * 0.24);

  const out: Rect[] = [];
  for (let i = 0; i < count; i++) {
    out.push(rect(startX, startY + i * (cardH + gap), cardW, cardH));
  }
  return out;
}

export function cellRect(l: Layout, cell: CellIndex): Rect {
  return rect(
    l.wellX + (cell % WELL_COLS) * l.cell,
    l.wellY + Math.floor(cell / WELL_COLS) * l.cell,
    l.cell,
    l.cell,
  );
}

export { WELL_COLS, WELL_ROWS };
